<template>
  <div
    class="dca-file-summary"
    :style="{ '--dca-file-summary-colour': moriartyColour }"
    data-testid="dca-litigation-file-summary"
  >
    <SoSkeleton
      v-if="isLoading"
      width="100%"
      height="72"
      data-testid="dca-litigation-file-summary-loading"
    />

    <p
      v-else-if="hasError"
      class="dca-file-summary__message"
      data-testid="dca-litigation-file-summary-error"
    >
      Could not load the latest Moriarty file status.
    </p>

    <p
      v-else-if="!summary.filename"
      class="dca-file-summary__message"
      data-testid="dca-litigation-file-summary-empty"
    >
      No Moriarty file has been processed yet.
    </p>

    <template v-else>
      <div class="dca-file-summary__header">
        <div class="dca-file-summary__icon">
          <SoIcon icon="file-text" size="small" />
        </div>
        <div>
          <div class="dca-file-summary__title-row">
            <span class="dca-file-summary__title"
              >Latest Moriarty file processed</span
            >
            <span class="dca-file-summary__date">{{ processedAtText }}</span>
          </div>
          <div class="dca-file-summary__filename text-caption">
            {{ summary.filename }}
          </div>
        </div>
      </div>

      <hr class="dca-file-summary__divider" />

      <div class="dca-file-summary__footer">
        <SoBadge
          class="dca-file-summary__chip dca-file-summary__chip--accepted"
          data-testid="dca-litigation-file-summary-accepted"
        >
          <span class="d-flex align-center ga-1">
            <SoIcon icon="check" size="x-small" />
            {{ summary.acceptedCount }} accepted
          </span>
        </SoBadge>

        <SoBadge
          class="dca-file-summary__chip dca-file-summary__chip--rejected"
          data-testid="dca-litigation-file-summary-rejected"
        >
          <span class="d-flex align-center ga-1">
            <SoIcon icon="alert-circle" size="x-small" />
            {{ summary.rejectedCount }} rejected
          </span>
        </SoBadge>

        <template v-if="summary.rejectedBreakdown.length > 0">
          <span class="dca-file-summary__footer-divider" />
          <span class="dca-file-summary__locked-label"
            >Rejected allocations:</span
          >
          <SoBadge
            v-for="entry in summary.rejectedBreakdown"
            :key="entry.dca"
            class="dca-file-summary__chip dca-file-summary__chip--locked"
            :style="{
              '--dca-file-summary-chip-colour': agencyColour(entry.dca),
            }"
            data-testid="dca-litigation-file-summary-locked-split"
          >
            <span class="d-flex align-center ga-1">
              <SoIcon icon="lock" size="x-small" />
              {{ agencyName(entry.dca) }} · {{ entry.count }}
            </span>
          </SoBadge>
        </template>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs from "dayjs"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoBadge from "@/components/SoBadge.vue"
  import { DcaLitigationFileSummaryService } from "@/services/DcaLitigationFileSummaryService"
  import { formatDateTime } from "@/helpers/dateHelper"
  import { agencyColour, agencyName, MORIARTY } from "../dcaAgencyColours"

  /**
   * Card for the most recently processed Moriarty litigation-placement-update file, so
   * ops can self-verify a file landed without asking engineering (SO-32859).
   *
   * Self-fetching, same as DcaAllocationFlagBadges - the rest of this page does not need
   * to know this exists. A failed or empty read renders as a one-line message rather than
   * an SoAlert: unlike the allocation config, nothing on this page depends on this data to
   * be safely editable, so there is nothing to block.
   *
   * The accepted/rejected/locked-split chips are SoBadge (the existing labelled-pill
   * wrapper) restyled to a tonal fill via :deep() rather than a new component - SoBadge's
   * default is an outlined chip, and success/success-highlight is the only existing
   * theme pair with a light-fill partner; error and the Moriarty purple have none, so
   * their fills are derived with color-mix instead of a matching design token. Worth
   * raising with design as a real gap - an error-highlight token would let the rejected
   * chip drop the color-mix override entirely.
   */
  export default defineComponent({
    components: { SoSkeleton, SoIcon, SoBadge },
    computed: {
      query() {
        return DcaLitigationFileSummaryService.getLatestFileSummary()
      },
      summary() {
        return this.query.data
      },
      isLoading(): boolean {
        return this.query.isFetching
      },
      hasError(): boolean {
        return this.query.isError
      },
      moriartyColour(): string {
        return agencyColour(MORIARTY)
      },
      processedAtText(): string {
        if (!this.summary.processedAt) {
          return ""
        }
        const at = dayjs(this.summary.processedAt)
        if (!at.isValid()) {
          return this.summary.processedAt
        }
        return formatDateTime(at)
      },
    },
    methods: {
      agencyName,
      agencyColour,
    },
  })
</script>

<style scoped>
  /* --dca-file-summary-colour is bound from agencyColour(MORIARTY) - see the script block,
     and matches DcaMoriartyGate so the icon and the locked-split chips read as the same
     feature. */
  .dca-file-summary {
    width: fit-content;
    max-width: 100%;
    padding: 16px 20px;
    background: rgb(var(--v-theme-surface-dim));
    border-radius: var(--theme-radius);
  }

  .dca-file-summary__message {
    margin: 0;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  .dca-file-summary__header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .dca-file-summary__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex: none;
    border-radius: 10px;
    background: color-mix(
      in oklab,
      var(--dca-file-summary-colour) 14%,
      rgb(var(--v-theme-surface))
    );
    color: var(--dca-file-summary-colour);
  }

  .dca-file-summary__title-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .dca-file-summary__title {
    font-weight: 700;
  }

  .dca-file-summary__date {
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  .dca-file-summary__filename {
    color: rgba(var(--v-theme-on-surface), 0.6);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  .dca-file-summary__divider {
    margin: 14px 0;
    border: none;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  }

  .dca-file-summary__footer {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .dca-file-summary__footer-divider {
    width: 1px;
    height: 20px;
    background: rgba(var(--v-theme-on-surface), 0.15);
  }

  .dca-file-summary__locked-label {
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  /*
   * SoBadge's root element IS the v-chip (class fallthrough lands the classes below
   * directly on it, not on a wrapper around it) - so these deep-scope the class itself
   * rather than a ".v-chip" descendant that doesn't exist. !important because Vuetify's
   * outlined variant sets border/text colour via inline style, which otherwise wins.
   */
  :deep(.dca-file-summary__chip) {
    border-width: 1px !important;
  }

  :deep(.dca-file-summary__chip--accepted) {
    color: rgb(var(--v-theme-success)) !important;
    border-color: rgb(var(--v-theme-success)) !important;
    background: rgb(var(--v-theme-success-highlight)) !important;
  }

  :deep(.dca-file-summary__chip--rejected) {
    color: rgb(var(--v-theme-error)) !important;
    border-color: rgb(var(--v-theme-error)) !important;
    background: color-mix(
      in oklab,
      rgb(var(--v-theme-error)) 14%,
      rgb(var(--v-theme-surface))
    ) !important;
  }

  /* --dca-file-summary-chip-colour is bound per chip from agencyColour(entry.dca) - see
     the template - so each rejected agency matches its own swatch in DcaAllocationLegend
     and the allocation matrix above, rather than sharing one colour. */
  :deep(.dca-file-summary__chip--locked) {
    color: var(--dca-file-summary-chip-colour) !important;
    border-color: var(--dca-file-summary-chip-colour) !important;
    background: color-mix(
      in oklab,
      var(--dca-file-summary-chip-colour) 14%,
      rgb(var(--v-theme-surface))
    ) !important;
  }
</style>
